// What the tags say about the work: for each tag group, how much of the work in scope is tagged, how the work (actions,
// hours, cost, carbon and items a year) splits between its tags, and which actions carry each tag. Built from the
// analysis graph of what is looked at (every action with its figures, roles and systems, as the workforce and systems
// reports use); the groups come from the organisation
import { AnalysisNode } from '../common/analysis-model';
import { ACTION_TYPE, NODE_SUPERTYPE } from '../common/linqset-base';
import { TAG_GROUP_ALL_MODELS, Tag, TagGroup } from '../common/organisation';
import { TAG_COLOURS } from '../organisation/tags/tag-colours';

export type TagMeasure = 'hours' | 'cost' | 'carbon' | 'items' | 'actions';

export const TAG_MEASURES: { key: TagMeasure; label: string; unit: string }[] = [
    { key: 'hours', label: 'Hours', unit: 'hours a year' },
    { key: 'cost', label: 'Cost', unit: 'a year' },
    { key: 'carbon', label: 'Carbon', unit: 'kg CO2e a year' },
    { key: 'items', label: 'Items', unit: 'items a year' },
    { key: 'actions', label: 'Actions', unit: 'actions' },
];

export const UNTAGGED_KEY = '';

export interface TaggedAction {
    key: string;
    name: string;
    modelKey: string;
    model: string;
    hours: number;
    cost: number;
    carbon: number;
    items: number;
    actionType: ACTION_TYPE;
    exceptionWork: boolean;             // on a detour from the main flow, or marked as exception work: friction
    roles: string[];
    systems: string[];
    tagByGroup: { [groupKey: string]: string };     // the tag it carries in each group
}

// the work carrying one tag (or none, for the untagged row)
export interface TagTotals {
    key: string;                        // the tag's key, or UNTAGGED_KEY
    name: string;
    description: string;
    tagIndex: number;
    colour: string;                     // the tag's colour; grey for untagged
    untagged: boolean;
    actions: number;
    hours: number;
    cost: number;
    carbon: number;
    items: number;
    automated: number;                  // actions done by a system alone
    exceptionActions: number;           // actions that are exception work, and their hours and cost
    exceptionHours: number;
    exceptionCost: number;
    models: string[];                   // the models the actions are in, most work first
    actionKeys: string[];
}

export interface TagGroupSummary {
    key: string;
    name: string;
    description: string;
    appliesToAll: boolean;              // the group is for every model, not a chosen few
    tags: TagTotals[];                  // the group's tags, most hours first, those with no work last
    untagged: TagTotals;                // the actions the group applies to that carry none of its tags
    total: number;                      // actions in scope the group applies to
    tagged: number;                     // of those, the actions with one of its tags
    coverage: number | null;            // tagged as a share of total, null with no actions
    hours: number;                      // of the tagged actions
    cost: number;
    carbon: number;
    items: number;
}

export interface TagsAnalysis {
    groups: TagGroupSummary[];          // the organisation's tag groups, most used first
    actions: TaggedAction[];
    totals: { actions: number; tagged: number; hours: number; taggedHours: number; cost: number; taggedCost: number; groupsInUse: number;
        exceptionActions: number; exceptionHours: number; exceptionCost: number };
}

export interface TagsAnalysisInput {
    nodes: AnalysisNode[];
    tagGroups: TagGroup[];
    modelNames: { [modelKey: string]: string };
    nameOf: (nodeKey: string) => string;       // a role's or system's name
}

export const tagColour = (tagIndex: number) => TAG_COLOURS[((tagIndex % TAG_COLOURS.length) + TAG_COLOURS.length) % TAG_COLOURS.length] || TAG_COLOURS[0];

// whether a group's tags can go on the actions of a model
export const groupAppliesToModel = (group: TagGroup, modelKey: string) =>
    !group.modelKeys || group.modelKeys.length === 0 || group.modelKeys.includes(TAG_GROUP_ALL_MODELS) || group.modelKeys.includes(modelKey);

const emptyTotals = (key: string, name: string, description: string, tagIndex: number, untagged: boolean): TagTotals => ({
    key, name, description, tagIndex, colour: untagged ? '#b8bdc9' : tagColour(tagIndex), untagged,
    actions: 0, hours: 0, cost: 0, carbon: 0, items: 0, automated: 0, exceptionActions: 0, exceptionHours: 0, exceptionCost: 0, models: [], actionKeys: [],
});

export const buildTagsAnalysis = (input: TagsAnalysisInput): TagsAnalysis => {
    const { modelNames, nameOf } = input;
    const unique = (names: string[]) => [...new Set(names.filter(n => !!n))];
    // the actions, each with its figures and the tag it carries in each group
    const actions: TaggedAction[] = input.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION).map(n => ({
        key: n._key, name: n.name || 'Unnamed', modelKey: n.modelKey, model: modelNames[n.modelKey] || 'Unnamed model',
        hours: n.hours || 0, cost: n.cost || 0, carbon: n.carbon || 0, items: n.items || 0,
        actionType: n.actionType ?? ACTION_TYPE.NONE,
        exceptionWork: !!n.exceptionWork,
        roles: unique((n.roles || []).map(r => nameOf(r._key))), systems: unique((n.systems || []).map(s => nameOf(s))),
        tagByGroup: { ...(n.tagKeys || {}) },
    }));

    const add = (t: TagTotals, a: TaggedAction, modelHours: Map<string, number>) => {
        t.actions++;
        t.hours += a.hours;
        t.cost += a.cost;
        t.carbon += a.carbon;
        t.items += a.items;
        if (a.actionType === ACTION_TYPE.AUTOMATED) t.automated++;
        if (a.exceptionWork) {
            t.exceptionActions++;
            t.exceptionHours += a.hours;
            t.exceptionCost += a.cost;
        }
        t.actionKeys.push(a.key);
        modelHours.set(a.model, (modelHours.get(a.model) || 0) + a.hours);
    };
    const modelsByWork = (modelHours: Map<string, number>) => [...modelHours.entries()].sort((a, b) => b[1] - a[1]).map(([m]) => m);

    const groups: TagGroupSummary[] = input.tagGroups.filter(g => !g.deleted).map(g => {
        const tags = new Map<string, { totals: TagTotals; models: Map<string, number> }>();
        (g.tags || []).forEach((t: Tag) => tags.set(t._key, { totals: emptyTotals(t._key, t.name, t.description || '', t.tagIndex, false), models: new Map() }));
        const untagged = { totals: emptyTotals(UNTAGGED_KEY, 'No tag', 'Actions with none of the group\'s tags', -1, true), models: new Map<string, number>() };
        let total = 0;
        actions.filter(a => groupAppliesToModel(g, a.modelKey)).forEach(a => {
            total++;
            const tagKey = a.tagByGroup[g._key];
            const tag = tagKey ? tags.get(tagKey) : undefined;
            if (tag) add(tag.totals, a, tag.models);
            else add(untagged.totals, a, untagged.models);
        });
        const list = [...tags.values()].map(t => ({ ...t.totals, models: modelsByWork(t.models) }))
            .sort((a, b) => b.hours - a.hours || b.actions - a.actions || a.name.localeCompare(b.name));
        const tagged = list.reduce((sum, t) => sum + t.actions, 0);
        return {
            key: g._key, name: g.name, description: g.description || '',
            appliesToAll: !g.modelKeys || g.modelKeys.length === 0 || g.modelKeys.includes(TAG_GROUP_ALL_MODELS),
            tags: list, untagged: { ...untagged.totals, models: modelsByWork(untagged.models) },
            total, tagged, coverage: total ? tagged / total : null,
            hours: list.reduce((sum, t) => sum + t.hours, 0), cost: list.reduce((sum, t) => sum + t.cost, 0),
            carbon: list.reduce((sum, t) => sum + t.carbon, 0), items: list.reduce((sum, t) => sum + t.items, 0),
        };
    }).sort((a, b) => b.tagged - a.tagged || (b.coverage || 0) - (a.coverage || 0) || a.name.localeCompare(b.name));

    const hasTag = (a: TaggedAction) => groups.some(g => g.tags.some(t => t.key === a.tagByGroup[g.key]));
    const taggedActions = actions.filter(hasTag);
    return {
        groups, actions,
        totals: {
            actions: actions.length, tagged: taggedActions.length,
            hours: actions.reduce((sum, a) => sum + a.hours, 0), taggedHours: taggedActions.reduce((sum, a) => sum + a.hours, 0),
            cost: actions.reduce((sum, a) => sum + a.cost, 0), taggedCost: taggedActions.reduce((sum, a) => sum + a.cost, 0),
            groupsInUse: groups.filter(g => g.tagged > 0).length,
            exceptionActions: actions.filter(a => a.exceptionWork).length,
            exceptionHours: actions.filter(a => a.exceptionWork).reduce((sum, a) => sum + a.hours, 0),
            exceptionCost: actions.filter(a => a.exceptionWork).reduce((sum, a) => sum + a.cost, 0),
        },
    };
};

// a tag's (or the untagged work's) figure for a measure
export const measureOf = (t: { actions: number; hours: number; cost: number; carbon: number; items: number }, measure: TagMeasure) => t[measure] || 0;
